import useNavigation from 'chameleon/ui-stack/hooks/useNavigation';
import HorizontalTabs from 'chameleon/ui-stack/ui/base/tabs/horizontal-tabs';
import ButtonWithIcon from 'chameleon/ui-stack/ui/buttons/button-with-icon';
import Header from 'chameleon/ui-stack/ui/page/header';
import Table from 'chameleon-src/components/ActivityResultsContainer/Table';
import { REPORTS_LABEL } from 'chameleon-src/components/SidebarWrapperTwoLevels/constants';
import { PE, PPC, QB } from 'chameleon-src/constants/subjects';
import { activityResultQuery } from 'chameleon-src/queries';
import { LoadingSpinner } from 'legacy-packages/ft-cb';
import Filters, { FilterControls } from 'legacy-src/utility/Filters';
import { reportsClient } from 'legacy-src/utility/GraphqlClients';
import React, { Fragment, useEffect, useState } from 'react';
import { compose, withApollo } from 'react-apollo';
import { connect } from 'react-redux';
import { isSubjectHasResource } from 'chameleon-src/utility/subjectResources';
import ExportDetails from './ExportDetails';
import ActivityReportTableLegend from './Table/ActivityReportTableLegend';

const ActivityResultsContainer = ({
  router,
  studentPracticeProblems,
  immediateFeedbackFlag,
  subjectData,
}) => {
  // create/move api wrapper based on axios, move saga and actions
  // load data and render the page components, use query-string to parse URL encoded-filters
  // move to useNavigation parsing and moving around history
  const {
    params: { subject: masterSubjectId },
  } = router;

  const [data, setData] = useState();
  const [isLoading, setLoading] = useState(false);
  const [showLegend, setShowLegend] = useState(false);
  const [enableExportDetails, setEnableExportDetails] = useState(false);

  const { pushParamsToHistory, queryParams } = useNavigation({
    router,
  });

  const {
    sectionId,
    educationPeriod,
    type: resourceType = '',
  } = queryParams || {};

  const fetchActivityResults = async (classId, subject, period) => {
    const {
      data: { activityResultByGroup: arData },
    } = await reportsClient.query({
      query: activityResultQuery,
      fetchPolicy: 'network-only',
      client: reportsClient,
      variables: {
        masterSubjectId: subject,
        classId: classId,
        educationPeriod: period,
      },
    });

    const parsedData = JSON.parse(arData['activityResultByGroup']);
    setData(parsedData);
    setEnableExportDetails(!parsedData.rows.length);
    setLoading(false);
  };
  useEffect(() => {
    if (sectionId) {
      setLoading(true);
      fetchActivityResults(sectionId, masterSubjectId, educationPeriod);
    }
  }, [masterSubjectId, sectionId]);

  const AP_FILTERS = [
    {
      name: 'All',
      value: 'all',
      values: ['ppc', 'qb', 'pe', 'video', 'practice-problems'],
      isVisible: () => true,
    },
    {
      name: 'Progress Checks',
      value: 'ppc',
      isVisible: (masterSubjectId) =>
        isSubjectHasResource(masterSubjectId, PPC),
    },
    {
      name: 'My Quizzes',
      value: 'qb',
      isVisible: (masterSubjectId) => isSubjectHasResource(masterSubjectId, QB),
    },
    {
      name: 'Practice Exams',
      value: 'pe',
      isVisible: (masterSubjectId) => isSubjectHasResource(masterSubjectId, PE),
    },
    {
      name: 'Videos',
      value: 'video',
      isVisible: () => true,
    },
    {
      name: 'Student Practice',
      value: 'practice-problems',
      isVisible: () => studentPracticeProblems,
    },
  ];

  const PREAP_FILTERS = [
    {
      name: 'All',
      value: 'all',
      values: ['learning-checkpoint', 'performance-task', 'teacher-authored'],
      isVisible: () => true,
    },
    {
      name: 'Learning Checkpoints',
      value: 'learning-checkpoint',
      isVisible: () => true,
    },
    {
      name: 'Performance Tasks',
      value: 'performance-task',
      isVisible: () => true,
    },
    {
      name: 'My Quizzes',
      value: 'teacher-authored',
      isVisible: () => true,
    },
  ];

  let typeFilters = subjectData.isPreApSubject ? PREAP_FILTERS : AP_FILTERS;

  return (
    <div className="mx-24 my-10">
      <Header subtitle={REPORTS_LABEL} title="All Assignments" />
      <FilterControls hideAllStudents disableAllSections hideAllDistricts>
        {({ filterProps, storedFilterProps }) => (
          <Fragment>
            <Filters {...storedFilterProps} />
            <div className="flex">
              <HorizontalTabs
                tabs={typeFilters
                  .filter((filter) => filter.isVisible(router.params.subject))
                  .map((filter) => ({
                    name: filter.name,
                    onClick: () => pushParamsToHistory({ type: filter.value }),
                    isActive:
                      filter.value === resourceType ||
                      (filter.values &&
                        filter.values.length &&
                        !filter.values.includes(resourceType)),
                  }))}
                tabsClassName="flex-1"
              />
              <div className="flex items-center space-x-3.5 justify-evenly">
                <ExportDetails
                  enableExportDetails={enableExportDetails}
                  router={router}
                  resourceType={resourceType}
                  filterProps={filterProps}
                />
                <ButtonWithIcon
                  isSecondary
                  onClick={() => setShowLegend(!showLegend)}
                  tabIndex="-1"
                  className="mt-5"
                  accessibility={{ title: 'Legend' }}
                  icon="legend"
                >
                  {showLegend ? 'Hide' : 'Show'} Legend
                </ButtonWithIcon>
              </div>
            </div>
            {showLegend && (
              <ActivityReportTableLegend
                subjectId={masterSubjectId}
                isSppEnabled={studentPracticeProblems}
              />
            )}
            {!isLoading && data ? (
              <Table
                key={`${sectionId}${resourceType}`}
                data={data}
                router={router}
                resourceType={resourceType}
                filterProps={filterProps}
                immediateFeedbackFlag={immediateFeedbackFlag}
              />
            ) : isLoading ? (
              <LoadingSpinner />
            ) : (
              <p className="text-center">—</p>
            )}
          </Fragment>
        )}
      </FilterControls>
    </div>
  );
};

function mapStateToProps(state) {
  const { featureFlag, subject } = state || {};
  const {
    student_practice_problems,
    spp_filter_by_subject,
    spp_filter_by_school,
    immediate_feedback,
  } = featureFlag || {};
  return {
    studentPracticeProblems:
      student_practice_problems ||
      (spp_filter_by_subject && spp_filter_by_school),
    immediateFeedbackFlag: immediate_feedback,
    subjectData: subject.data,
  };
}

export default compose(
  withApollo,
  connect(mapStateToProps),
)(ActivityResultsContainer);
